import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CONTACT, waLink } from "@/lib/config";
import { CheckCircle2, Loader2, CalendarDays } from "lucide-react";

const TIMES = ["10:00", "11:00", "12:00", "14:00", "15:00", "16:00", "17:00", "18:00"];

export default function AppointmentForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", date: "", time: "", message: "" });
  const [services, setServices] = useState([]);
  const [status, setStatus] = useState({ loading: false, ok: false, err: "" });

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const buildWa = () => {
    const lines = [
      "New appointment booking from KK Associates website:",
      `Name: ${form.name}`,
      `Phone/WhatsApp: ${form.phone}`,
      `Email: ${form.email}`,
      `Service: ${form.service}`,
      `Date & Time: ${form.date} at ${form.time}`,
      form.message ? `Note: ${form.message}` : "",
    ].filter(Boolean);
    return waLink(lines.join("\n"));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.phone || form.phone.trim().length < 6) {
      setStatus({ loading: false, ok: false, err: "Phone / WhatsApp number is required." });
      return;
    }
    setStatus({ loading: true, ok: false, err: "" });
    try {
      await api.post("/appointments", form);
      window.open(buildWa(), "_blank", "noopener,noreferrer");
      setStatus({ loading: false, ok: true, err: "" });
      setForm({ name: "", email: "", phone: "", service: "", date: "", time: "", message: "" });
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join(" ") : "Something went wrong.";
      setStatus({ loading: false, ok: false, err: msg });
    }
  };

  const today = new Date().toISOString().split("T")[0];
  const input =
    "w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none transition-colors";

  if (status.ok) {
    return (
      <div data-testid="appointment-success" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-emerald-900">
        <div className="flex items-center gap-2 font-medium"><CheckCircle2 size={18} /> Appointment requested</div>
        <p className="mt-2 text-sm">A WhatsApp window has opened — just tap send to instantly confirm on {CONTACT.phone}.</p>
        <button onClick={() => setStatus({ loading: false, ok: false, err: "" })} className="mt-4 text-sm text-emerald-800 underline underline-offset-2">Book another</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="appointment-form" className="grid grid-cols-1 gap-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input required data-testid="appt-name" placeholder="Your name *" className={input} value={form.name} onChange={update("name")} />
        <input required type="email" data-testid="appt-email" placeholder="Email address *" className={input} value={form.email} onChange={update("email")} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input required minLength={6} inputMode="tel" data-testid="appt-phone" placeholder="Phone / WhatsApp number *" className={input} value={form.phone} onChange={update("phone")} />
        <select required data-testid="appt-service" className={input} value={form.service} onChange={update("service")}>
          <option value="">Select service *</option>
          {services.map((s) => <option key={s.id} value={s.title}>{s.title}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input required type="date" min={today} data-testid="appt-date" className={input} value={form.date} onChange={update("date")} />
        <select required data-testid="appt-time" className={input} value={form.time} onChange={update("time")}>
          <option value="">Preferred time *</option>
          {TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      <textarea data-testid="appt-message" rows={3} placeholder="Anything we should know? (optional)" className={input} value={form.message} onChange={update("message")} />
      <p className="text-xs text-slate-500">A WhatsApp window will open pre-filled to {CONTACT.phone} after you submit — tap send to confirm faster.</p>
      {status.err && <div className="text-sm text-red-600" data-testid="appt-error">{status.err}</div>}
      <button
        type="submit"
        disabled={status.loading}
        data-testid="appt-submit"
        className="btn-hover inline-flex items-center justify-center gap-2 rounded-full bg-blue-700 text-white px-6 py-3 text-sm font-medium hover:bg-blue-800 disabled:opacity-60"
      >
        {status.loading ? <><Loader2 size={16} className="animate-spin" /> Booking…</> : <><CalendarDays size={16} /> Book & WhatsApp</>}
      </button>
    </form>
  );
}
